import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Form, Row, Col } from 'react-bootstrap';
import { deliveryNoteService } from '../../services/api';
import { DeliveryNote } from '../../types';
import { toast } from 'react-toastify';
import { PlusIcon, PdfIcon, EditIcon, TrashIcon } from '../common/Icons';
import DeliveryNoteForm from './DeliveryNoteForm';

const DeliveryNoteList: React.FC = () => {
  const [deliveryNotes, setDeliveryNotes] = useState<DeliveryNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNote, setEditingNote] = useState<DeliveryNote | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
  });

  useEffect(() => {
    fetchDeliveryNotes();
  }, [filters]);

  const fetchDeliveryNotes = async () => {
    try {
      setLoading(true);
      const response = await deliveryNoteService.getAll(filters);
      setDeliveryNotes(response.data.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des bons de livraison');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce bon de livraison ?')) {
      try {
        await deliveryNoteService.delete(id);
        toast.success('Bon de livraison supprimé avec succès');
        fetchDeliveryNotes();
      } catch (error) {
        toast.error('Erreur lors de la suppression');
      }
    }
  };


    const handleExportPdf = async (note: DeliveryNote) => {
      try {
        const response = await deliveryNoteService.generatePdf(note.id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `facture-${note.delivery_number}.pdf`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast.error('Erreur lors de l\'export PDF');
      }
    };
  
  const handleStatusChange = async (id: number, status: string) => {
    try {
      await deliveryNoteService.updateStatus(id, status);
      toast.success('Statut mis à jour avec succès');
      fetchDeliveryNotes();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: { [key: string]: string } = {
      draft: 'secondary',
      sent: 'primary',
      delivered: 'success',
      cancelled: 'danger',
    };
    
    const labels: { [key: string]: string } = {
      draft: 'Brouillon',
      sent: 'Envoyé',
      delivered: 'Livré',
      cancelled: 'Annulé',
    };

    return <Badge bg={variants[status] || 'secondary'}>{labels[status] || status}</Badge>;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Bons de livraison</h2>
        <Button variant="primary" onClick={() => setShowForm(true)}>
          <PlusIcon className="me-2" />
          Nouveau bon de livraison
        </Button>
      </div>

      <Card>
        <Card.Header>
          <Row>
            <Col md={4}>
              <Form.Control
                type="text"
                placeholder="Rechercher..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">Tous les statuts</option>
                <option value="draft">Brouillon</option>
                <option value="sent">Envoyé</option>
                <option value="delivered">Livré</option>
                <option value="cancelled">Annulé</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center">Chargement...</div>
          ) : (
            <Table responsive>
              <thead>
                <tr>
                  <th>Numéro</th>
                  <th>Client</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th>Total TTC</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {deliveryNotes.map((note) => (
                  <tr key={note.id}>
                    <td>{note.delivery_number}</td>
                    <td>{note.client?.name}</td>
                    <td>{new Date(note.date).toLocaleDateString()}</td>
                    <td>
                      <Form.Select
                        size="sm"
                        value={note.status}
                        onChange={(e) => handleStatusChange(note.id, e.target.value)}
                        style={{ width: 'auto', display: 'inline-block' }}
                      >
                        <option value="draft">Brouillon</option>
                        <option value="sent">Envoyé</option>
                        <option value="delivered">Livré</option>
                        <option value="cancelled">Annulé</option>
                      </Form.Select>
                    </td>
                    <td>{Number(note.total_ttc || 0).toFixed(2)} MAD</td>
                    <td>
                        <Button 
                                              variant="outline-success" 
                                              size="sm" 
                                              className="me-1"
                                              onClick={() => handleExportPdf(note)}
                                              title="Export PDF"
                                            >
                                                <PdfIcon />
                                            </Button>
                    
                      <Button 
                        variant="outline-warning" 
                        size="sm" 
                        className="me-1"
                        onClick={() => {
                          setEditingNote(note);
                          setShowForm(true);
                        }}
                      >
                        <EditIcon />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDelete(note.id)}
                      >
                        <TrashIcon />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <DeliveryNoteForm
        show={showForm}
        onHide={() => {
          setShowForm(false);
          setEditingNote(null);
        }}
        onSuccess={() => {
          fetchDeliveryNotes();
          setShowForm(false);
          setEditingNote(null);
        }}
        deliveryNote={editingNote}
      />
    </div>
  );
};

export default DeliveryNoteList;